import {useEffect, useState} from 'react';
import {Button, FormControl, Modal} from '@goproblems/ui';

import {hideModal, useSelector} from '@utils';
import {MODAL_SUGGESTED_EDITS_PREVIEW} from '@utils';
import {useTranslation} from 'react-i18next';
import {useLazyFetchSgfDiffQuery} from '@goproblems/shared/api/tools';
import {Loading, SgfDiffContent} from '@components';
import {SuggestedProblemEditStatusEnum} from '@types';

export const SuggestedEditsPreviewModal = ({
    name = MODAL_SUGGESTED_EDITS_PREVIEW,
    originalSgf,
    suggestedSgf,
    status = SuggestedProblemEditStatusEnum.OPEN,
    comment: commentProp,
    variant = 'new',
    onSubmit,
    onApprove,
    onReject,
}: {
    name?: string;
    variant: 'new' | 'edit' | 'resolve' | 'view';
    originalSgf?: string;
    suggestedSgf: string;
    status?: SuggestedProblemEditStatusEnum;
    comment?: string;
    onSubmit: (comment: string) => void;
    onApprove?: (resolutionComment: string) => void;
    onReject?: (resolutionComment: string) => void;
}) => {
    const [resolutionComment, setResolutionComment] = useState('');
    const [comment, setComment] = useState<string>(commentProp || '');
    const show = useSelector(s => s.modalSuggestedEditsPreview);
    const {t} = useTranslation();
    const [fetchSgfData, {data: sgfDiffs, isLoading}] =
        useLazyFetchSgfDiffQuery();

    const handleClose = () => {
        hideModal(name);
    };

    const handleSubmit = () => {
        if (onSubmit) onSubmit(comment);
    };

    const handleReject = () => {
        if (onReject) onReject(resolutionComment);
    };

    const handleApprove = () => {
        if (onApprove) onApprove(resolutionComment);
    };

    useEffect(() => {
        if (show && originalSgf && suggestedSgf) {
            void fetchSgfData({sgf1: originalSgf, sgf2: suggestedSgf});
        }
    }, [show, originalSgf, suggestedSgf, fetchSgfData]);

    return (
        <Modal
            className="suggested-edits-preview-modal"
            show={show}
            onHide={handleClose}
            dialogClassName="md:w-[fit-content] md:min-w-[750px]">
            <Modal.Header closeButton>
                <Modal.Title>{t('suggestions.preview')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading && <Loading />}
                {sgfDiffs?.length === 0 && (
                    <p>{t('suggestions.no_differences_found')}</p>
                )}
                {sgfDiffs && sgfDiffs?.length > 0 && (
                    <SgfDiffContent
                        originalSgf={originalSgf}
                        sgfDiffs={sgfDiffs}
                    />
                )}
                {variant === 'resolve' &&
                    status === SuggestedProblemEditStatusEnum.OPEN && (
                        <>
                            <span className="font-semibold">
                                {t('suggestions.resolution_comment')}
                            </span>
                            <FormControl
                                type="text"
                                placeholder={t(
                                    'suggestions.resolution_comment_placeholder'
                                )}
                                value={resolutionComment}
                                onChange={e =>
                                    setResolutionComment(e.target.value)
                                }
                            />
                        </>
                    )}
                {(variant === 'new' || variant === 'edit') && (
                    <>
                        <span className="font-semibold">
                            {t('suggestions.suggestion_comment')}
                        </span>
                        <FormControl
                            type="text"
                            placeholder={t(
                                'suggestions.suggestion_comment_placeholder'
                            )}
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {(variant === 'new' || variant === 'edit') && (
                    <>
                        <Button variant="secondary" onClick={handleClose}>
                            {t('common.close')}
                        </Button>
                        <Button variant="primary" onClick={handleSubmit}>
                            {variant === 'edit'
                                ? t('common.update')
                                : t('common.submit')}
                        </Button>
                    </>
                )}
                {variant === 'resolve' && (
                    <div className="flex justify-between flex-1">
                        <div>
                            <Button variant="secondary" onClick={handleClose}>
                                {t('common.close')}
                            </Button>
                        </div>
                        {status &&
                            status === SuggestedProblemEditStatusEnum.OPEN && (
                                <div className="flex gap-2">
                                    <Button
                                        variant="danger"
                                        onClick={handleReject}>
                                        {t('suggestions.reject')}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={handleApprove}>
                                        {t('suggestions.approve')}
                                    </Button>
                                </div>
                            )}
                    </div>
                )}
            </Modal.Footer>
        </Modal>
    );
};
